/**
 * Centralised endpoint paths — never duplicate URL strings in services.
 */
export const endpoints = {
  auth: {
    otpRequest: '/auth/otp/request',
    otpVerify: '/auth/otp/verify',
  },
  /**
   * Shot/delivery taxonomy used to populate the per-slot "Target shot(s)"
   * filter. Not tenant-admin scoped; `:discipline` is BATTING | BOWLING.
   */
  videoActionCategories: {
    byDiscipline: (discipline: string) => `/api/v2/video-action-categories/${discipline}`,
  },
  notifications: {
    inbox: '/api/v2/notifications',
    unreadCount: '/api/v2/notifications/unread-count',
    read: (id: number | string) => `/api/v2/notifications/${id}/read`,
    opened: (id: number | string) => `/api/v2/notifications/${id}/opened`,
    readAll: '/api/v2/notifications/read-all',
  },
  admin: {
    overview: '/api/v2/tenant-admin/overview',

    /** Dropdown source — hit once on dashboard load, cache 5 min. */
    filterOptions: '/api/v2/tenant-admin/filter-options',

    admins: '/api/v2/tenant-admin/admins',

    users: '/api/v2/tenant-admin/users',
    userDetail: (userId: number | string) => `/api/v2/tenant-admin/users/${userId}`,
    userHistory: (userId: number | string) => `/api/v2/tenant-admin/users/${userId}/history`,
    userProfile: (userId: number | string) => `/api/v2/tenant-admin/users/${userId}/profile`,
    userMembership: (userId: number | string) => `/api/v2/tenant-admin/users/${userId}/membership`,
    userProgressAdjustments: (userId: number | string) =>
      `/api/v2/tenant-admin/users/${userId}/progress-adjustments`,
    userEntitlementAdjustments: (userId: number | string) =>
      `/api/v2/tenant-admin/users/${userId}/entitlement-adjustments`,

    challenges: '/api/v2/tenant-admin/challenges',
    /** Scoring v2 authoring catalog: falloff variants, defaults, deviation presets, age-pace table. */
    challengeScoringCatalog: '/api/v2/tenant-admin/challenges/scoring-catalog',
    challenge: (id: number | string) => `/api/v2/tenant-admin/challenges/${id}`,
    challengeArchive: (id: number | string) => `/api/v2/tenant-admin/challenges/${id}/archive`,

    leagues: '/api/v2/tenant-admin/leagues',
    league: (id: number | string) => `/api/v2/tenant-admin/leagues/${id}`,
    leagueArchive: (id: number | string) => `/api/v2/tenant-admin/leagues/${id}/archive`,
    /** CSV of participants plus anyone who answered the entry consent screen. */
    leagueParticipantsExport: (id: number | string) =>
      `/api/v2/tenant-admin/leagues/${id}/participants/export`,
    /** Sponsor export (CSV) of everything we know about a league's players. */
    leagueSponsorExport: (id: number | string) =>
      `/api/v2/tenant-admin/leagues/${id}/sponsor-export`,
    /** Sponsor export (CSV) of everything we know about a challenge's players. */
    challengeSponsorExport: (id: number | string) =>
      `/api/v2/tenant-admin/challenges/${id}/sponsor-export`,
    leagueIconUploadUrl: '/api/v2/tenant-admin/leagues/icon-upload-url',
    leagueIconPresets: '/api/v2/tenant-admin/leagues/icon-presets',
    challengeIconUploadUrl: '/api/v2/tenant-admin/challenges/icon-upload-url',

    quizzes: '/api/v2/tenant-admin/quizzes',
    quiz: (id: number | string) => `/api/v2/tenant-admin/quizzes/${id}`,
    quizArchive: (id: number | string) => `/api/v2/tenant-admin/quizzes/${id}/archive`,
    quizParticipants: (id: number | string) => `/api/v2/tenant-admin/quizzes/${id}/participants`,
    quizAttempt: (id: number | string, attemptId: number | string) =>
      `/api/v2/tenant-admin/quizzes/${id}/attempts/${attemptId}`,
    quizLeaderboard: (id: number | string) => `/api/v2/tenant-admin/quizzes/${id}/leaderboard`,

    polls: '/api/v2/tenant-admin/polls',
    poll: (id: number | string) => `/api/v2/tenant-admin/polls/${id}`,
    pollParticipants: (id: number | string) => `/api/v2/tenant-admin/polls/${id}/participants`,
    pollResponse: (id: number | string, responseId: number | string) =>
      `/api/v2/tenant-admin/polls/${id}/responses/${responseId}`,
    pollLeaderboard: (id: number | string) => `/api/v2/tenant-admin/polls/${id}/leaderboard`,
    pollArchive: (id: number | string) => `/api/v2/tenant-admin/polls/${id}/archive`,

    plans: '/api/v2/tenant-admin/plans',

    // ─── Billing ─────────────────────────────────────────────────────────
    subscriptions: '/api/v2/tenant-admin/subscriptions',
    purchases: '/api/v2/tenant-admin/purchases',
    userBilling: (userId: number | string) => `/api/v2/tenant-admin/users/${userId}/billing`,
    cancelSubscription: (userId: number | string, subscriptionId: number | string) =>
      `/api/v2/tenant-admin/users/${userId}/subscriptions/${subscriptionId}/cancel`,
    refundPurchase: (userId: number | string, purchaseId: number | string) =>
      `/api/v2/tenant-admin/users/${userId}/purchases/${purchaseId}/refund`,
    grantSubscription: (userId: number | string) =>
      `/api/v2/tenant-admin/users/${userId}/grant-subscription`,
    revenueCatSync: (userId: number | string) =>
      `/api/v2/tenant-admin/users/${userId}/revenuecat-sync`,

    // ─── Integrations / webhook events ───────────────────────────────────
    webhookEvents: '/api/v2/tenant-admin/webhook-events',
    replayWebhookEvent: (id: number | string) =>
      `/api/v2/tenant-admin/webhook-events/${id}/replay`,

    sponsors: '/api/v2/tenant-admin/sponsors',
    sponsor: (id: number | string) => `/api/v2/tenant-admin/sponsors/${id}`,
    sponsorUploadUrl: '/api/v2/tenant-admin/sponsors/upload-url',

    /**
     * VTEX-04 — editable How to Play content. NOTE the different prefix: this
     * lives on the `admin` router (/api/v2/admin/...), not the tenant-admin one.
     */
    howToPlay: '/api/v2/admin/content/how-to-play',
    /**
     * Tenant default for the league "How it works" card. Same `admin` router
     * prefix as howToPlay. PUT `{ how_it_works: {...} | null }` (null resets
     * to the platform default).
     */
    leagueHowItWorks: '/api/v2/admin/content/league-how-it-works',

    /** VTEX-10 — admin dashboard. Same `admin` router prefix as howToPlay. */
    dashboardKpis: '/api/v2/admin/dashboard/kpis',
    dashboardRevealQueue: '/api/v2/admin/dashboard/reveal-queue',
    dashboardLeaguePerformance: '/api/v2/admin/dashboard/league-performance',
    dashboardChallengePerformance: '/api/v2/admin/dashboard/challenge-performance',
    /** League drill-down: summary, per-stage stats and the leaderboard. */
    dashboardLeagueDetail: (id: number | string) => `/api/v2/admin/dashboard/leagues/${id}`,
    dashboardLeagueSubmissions: (id: number | string) =>
      `/api/v2/admin/dashboard/leagues/${id}/submissions`,
    /** Challenge drill-down: summary, score distribution, daily volume, leaderboard. */
    dashboardChallengeDetail: (id: number | string) => `/api/v2/admin/dashboard/challenges/${id}`,
    dashboardChallengeSubmissions: (id: number | string) =>
      `/api/v2/admin/dashboard/challenges/${id}/submissions`,

    notificationsSend: '/api/v2/tenant-admin/notifications/send',
    notificationsPushTest: '/api/v2/tenant-admin/notifications/push-test',
    notificationDeliver: (id: number | string) =>
      `/api/v2/tenant-admin/notifications/${id}/deliver`,
    videoLabelling: (id: number | string) => `/api/v2/videos/${id}/labelling`,
    submissions: '/api/v2/submissions',
    submissionDetail: (id: number | string) => `/api/v2/submissions/${id}`,
    // Admin submission auditing: manual override, raw-metric correction, audit log.
    submissionOverride: (id: number | string) => `/api/v2/submissions/${id}/override`,
    submissionCorrectMetrics: (id: number | string) => `/api/v2/submissions/${id}/correct-metrics`,
    submissionAudit: (id: number | string) => `/api/v2/submissions/${id}/audit`,
    /** Re-run the latest scoring on a submission (tenant admin). Body { notify }. */
    submissionRescore: (id: number | string) => `/api/v2/submissions/${id}/rescore`,
    /** Read-only replay of the scoring engine for one submission (tenant admin). */
    submissionScoringExplanation: (id: number | string) =>
      `/api/v2/submissions/${id}/scoring-explanation`,
    playerAssessments: (playerId: number | string) => `/api/v2/assessment/player/${playerId}`,
    assessmentDetail: (id: number | string) => `/api/v2/assessment/${id}`,
    assessmentPdf: (id: number | string) => `/api/v2/assessment/pdf-view/${id}`,
  },
} as const;
